import {
  ArrowRightLeft,
  Ban,
  CalendarCheck,
  CalendarCheck2,
  CalendarClock,
  CheckCircle2,
  CircleCheckBig,
  Clock,
  FileClock,
  FilePen,
  Info,
  Repeat,
  UserCheck,
  X,
} from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';

import type { GetCurrentUser, GetReservationById } from '@virtual-care/client';
import {
  displayName,
  formatDate,
  formatReservationDate,
  formatReservationTime,
  getLocalizedSpeciality,
  getPatientDemographics,
  toAge,
} from '@virtual-care/isomorphic';
import { AttachmentsDialog } from '@virtual-care/media-web';
import {
  AdditionalInfo,
  AppointmentHeader,
  AppointmentTimeline,
  Loading,
  PatientInfoCard,
  PaymentDetailsCard,
  ReservationFinished,
  useData,
} from '@virtual-care/ui';

import { useLanguage } from '../../../i18n/use-language.ts';
import { Header, ProfileCard } from '../../components/header/index.ts';
import { usePaymentInfo } from '../../hooks/use-payment-info.ts';
import { CancelButton } from './actions/cancel-action.tsx';
import { FollowupButton } from './actions/followup-action.tsx';
import { JoinButton } from './actions/join-action.tsx';
import { SubmitInfoButton } from './actions/submit-info-action.tsx';
import { AppointmentCard } from './appointment-card.tsx';
import { DiagnosisCards } from './diagnosis-card.tsx';
import type { StatusMap, TimelineMap } from './status.ts';

const minutesBefore = 15;

function useStatusMap({
  reservation,
  canceledDate,
}: {
  reservation?: GetReservationById;
  canceledDate?: string;
}): StatusMap {
  const { t } = useTranslation();
  if (!reservation) {
    return {} as StatusMap;
  }
  const formattedDate = formatReservationDate(reservation);
  const formattedTime = formatReservationTime(reservation);
  return {
    WAITING_DOCTOR_ASSIGNMENT: {
      text: t('appointments.detail.statuses.waitingDoctorAssignment'),
      icon: Info,
      className: 'border-orange-300 bg-orange-50 text-orange-700',
    },
    WAITING_DOCTOR_CONFIRMATION: {
      text: t('appointments.detail.statuses.waitingDoctorConfirmation'),
      icon: Clock,
      className: 'border-blue-300 bg-blue-50 text-blue-700',
    },
    CONFIRMED: {
      text: t('appointments.detail.statuses.confirmed', {
        date: formattedDate,
        time: formattedTime,
      }),
      icon: CheckCircle2,
      className: 'border-green-300 bg-green-50 text-green-700',
    },
    FINISHED: {
      text: t('appointments.detail.statuses.finished', {
        date: formattedDate,
        time: formattedTime,
      }),
      icon: CalendarCheck,
      className: 'border-border bg-muted text-muted-foreground',
    },
    CANCELED: {
      text: t('appointments.detail.statuses.canceledOn', {
        date: canceledDate,
      }),
      icon: Ban,
      className: 'border-red-300 bg-red-50 text-red-700',
    },
    AVAILABLE_FOR_FOLLOWUP: {
      text: t('appointments.detail.statuses.finished', {
        date: formattedDate,
        time: formattedTime,
      }),
      icon: CalendarCheck,
      className: 'border-border bg-muted text-muted-foreground',
    },
    EXPIRED: {
      text: t('appointments.detail.statuses.expired'),
      icon: FileClock,
      className: 'border-gray-300 bg-gray-50 text-gray-700',
    },
  };
}
function useHistoryEventDetails({
  reservation,
}: {
  reservation?: GetReservationById;
}): TimelineMap {
  const { t } = useTranslation();
  if (!reservation) {
    return {} as TimelineMap;
  }
  const formattedDate = formatReservationDate(reservation);
  const formattedTime = formatReservationTime(reservation);
  return {
    RESERVATION_CREATED: {
      icon: FileClock,
      color: 'text-blue-500',
      title: t('appointments.detail.timeline.reservationCreated.title', {
        number: reservation.reservationNumber,
      }),
      message: t('appointments.detail.timeline.reservationCreated.message'),
    },
    RESERVATION_CANCELED: {
      icon: Ban,
      color: 'text-red-500',
      title: t('appointments.detail.timeline.reservationCanceled.title'),
      message: t('appointments.detail.timeline.reservationCanceled.message'),
    },
    RESERVATION_FINISHED: {
      icon: CalendarCheck,
      color: 'text-blue-500',
      title: t('appointments.detail.timeline.reservationFinished.title'),
      message: t('appointments.detail.timeline.reservationFinished.message'),
    },
    DOCTOR_ASSIGNED: {
      icon: CircleCheckBig,
      color: 'text-green-500',
      title: t('appointments.detail.timeline.doctorAssigned.title'),
      message: t('appointments.detail.timeline.doctorAssigned.message', {
        name: `${reservation.doctor?.firstName || ''} ${reservation.doctor?.lastName || ''}`.trim(),
      }),
    },
    APPOINTMENT_CONFIRMED_BY_DOCTOR: {
      icon: CalendarCheck2,
      color: 'text-chart-1',
      title: t('appointments.detail.timeline.appointmentConfirmed.title'),
      message: t('appointments.detail.timeline.appointmentConfirmed.message', {
        date: formattedDate,
        time: formattedTime,
      }),
    },
    APPOINTMENT_WAITING_FOR_DOCTOR_CONFIRMATION: {
      icon: CalendarClock,
      color: 'text-orange-500',
      title: t('appointments.detail.timeline.waitingDoctorConfirmation.title'),
      message: t(
        'appointments.detail.timeline.waitingDoctorConfirmation.message',
      ),
    },
    INFO_SUBMITTED: {
      icon: FilePen,
      color: 'text-blue-500',
      title: t('appointments.detail.timeline.infoSubmitted.title'),
      message: t('appointments.detail.timeline.infoSubmitted.message'),
    },
    MORE_INFO_NEEDED: {
      icon: Info,
      color: 'text-orange-500',
      title: t('appointments.detail.timeline.moreInfoNeeded.title'),
      message: t('appointments.detail.timeline.moreInfoNeeded.message'),
    },
    PATIENT_JOINED_CALL: {
      icon: UserCheck,
      color: 'text-green-500',
      title: t('appointments.detail.timeline.patientJoinedCall.title'),
      message: t('appointments.detail.timeline.patientJoinedCall.message'),
    },
    DOCTOR_JOINED_CALL: {
      icon: UserCheck,
      color: 'text-green-500',
      title: t('appointments.detail.timeline.doctorJoinedCall.title'),
      message: t('appointments.detail.timeline.doctorJoinedCall.message'),
    },
    REFERRED_TO_ANOTHER_DOCTOR: {
      icon: ArrowRightLeft,
      color: 'text-blue-500',
      title: t('appointments.detail.timeline.referred.title'),
      message: t('appointments.detail.timeline.referred.message'),
    },
    MARKED_AVAILABLE_FOR_FOLLOWUP: {
      icon: Repeat,
      color: 'text-cyan-500',
      title: t('appointments.detail.timeline.markedAvailableForFollowup.title'),
      message: t(
        'appointments.detail.timeline.markedAvailableForFollowup.message',
      ),
    },
    FOLLOWUP_WINDOW_EXPIRED: {
      icon: FileClock,
      color: 'text-gray-500',
      title: t('appointments.detail.timeline.followupWindowExpired.title'),
      message: t('appointments.detail.timeline.followupWindowExpired.message'),
    },
    FOLLOWUP_TIME_REJECTED: {
      icon: X,
      color: 'text-orange-500',
      title: t('appointments.detail.timeline.followupTimeRejected.title'),
      message: t('appointments.detail.timeline.followupTimeRejected.message'),
    },
  };
}

export default function Appointment() {
  const params = useParams();
  const { t } = useTranslation();
  const { language } = useLanguage();
  const [isAttachmentsDialogOpen, setIsAttachmentsDialogOpen] = useState(false);
  const payment = usePaymentInfo({ kind: 'standard' });
  const {
    data: reservation,
    isLoading,
    refetch,
  } = useData('GET /reservations/{id}', { id: params.id! });

  const { data: diagnoses, isLoading: isDiagnosesLoading } = useData(
    'GET /reservations/{id}/diagnoses',
    { id: params.id! },
    {
      enabled:
        reservation?.status === 'AVAILABLE_FOR_FOLLOWUP' ||
        reservation?.status === 'FINISHED',
    },
  );

  const canceledEvent = reservation?.timeline.find(
    (event) => event.type === 'RESERVATION_CANCELED',
  );

  const moreInfoNeeded = reservation?.timeline.find(
    (event) => event.type === 'MORE_INFO_NEEDED',
  );

  const lastTimelineEvent = reservation?.timeline[0];
  const infoSubmittedAfterRequest =
    moreInfoNeeded && lastTimelineEvent?.type === 'INFO_SUBMITTED';

  const statusMap = useStatusMap({
    reservation,
    canceledDate: canceledEvent ? formatDate(canceledEvent.createdAt) : '',
  });

  const historyEventDetails = useHistoryEventDetails({ reservation });

  if (isLoading || isDiagnosesLoading) return <Loading />;

  if (!reservation) {
    return <div>{t('appointments.detail.notFound')}</div>;
  }

  const reservationCreated = reservation.timeline.find(
    (it) => it.type === 'RESERVATION_CREATED',
  )!;
  const reservationContext = JSON.parse(reservationCreated.context) as {
    patient: GetCurrentUser;
  };

  const demographics = getPatientDemographics(
    reservation.timeline,
    'RESERVATION_CREATED',
  );
  const speciality = getLocalizedSpeciality(reservation.speciality, language);

  const patientSummary = [
    {
      label: t('appointments.detail.patientSummary.name'),
      value: demographics?.name ?? displayName(reservationContext.patient),
    },
    {
      show: demographics
        ? !!demographics.birthDate
        : reservationContext.patient.b2cProfile != null,
      label: t('appointments.detail.patientSummary.age'),
      value: demographics?.birthDate
        ? `${toAge({ birthDate: demographics.birthDate })}`
        : reservationContext.patient.b2cProfile
          ? `${toAge(reservationContext.patient.b2cProfile)}`
          : t('common.notSpecified'),
    },
    {
      show: demographics
        ? !!demographics.gender
        : reservationContext.patient.b2cProfile != null,
      label: t('appointments.detail.patientSummary.gender'),
      value: (() => {
        const g =
          demographics?.gender ?? reservationContext.patient.b2cProfile?.gender;
        return g === 'male' || g === 'female' ? t(`common.${g}`) : '';
      })(),
    },
    {
      label: t('appointments.detail.patientSummary.specialty'),
      value: speciality.name,
    },
    {
      label: t('appointments.detail.patientSummary.attachments'),
      value: `${reservation.files.length > 0 ? `+${reservation.files.length}` : t('appointments.detail.patientSummary.noAttachments')}`,
    },
  ].filter((item) => item.show !== false);

  const isB2BPatient = !reservationContext.patient.b2cProfile;

  return (
    <>
      <Header
        title={
          <AppointmentHeader
            reservationNumber={reservation.reservationNumber}
            status={reservation.status}
            showBackButton={true}
            price={isB2BPatient ? undefined : payment.amountDue.shortLabel}
          />
        }
      >
        <div className="hidden md:block">
          <ProfileCard />
        </div>
      </Header>
      <div className="min-h-screen px-4 sm:px-6 lg:px-8">
        <div className="mx-auto grid max-w-7xl grid-cols-1 gap-6 lg:grid-cols-3">
          <div className="col-span-1 flex flex-col gap-6 lg:col-span-2">
            <AppointmentCard
              reservation={reservation}
              statusInfoMap={statusMap}
              patientSummary={patientSummary}
              onViewAttachments={() => setIsAttachmentsDialogOpen(true)}
              totalAmount={
                isB2BPatient ? undefined : payment.amountDue.shortLabel
              }
              footer={
                <>
                  {reservation.status === 'AVAILABLE_FOR_FOLLOWUP' && (
                    <FollowupButton
                      reservation={reservation}
                      onSuccess={refetch}
                    />
                  )}
                  {(reservation.status === 'WAITING_DOCTOR_ASSIGNMENT' ||
                    reservation.status === 'WAITING_DOCTOR_CONFIRMATION') && (
                    <div className="flex justify-end">
                      <CancelButton
                        reservationId={reservation.id}
                        reservationNumber={reservation.reservationNumber}
                        isB2B={isB2BPatient}
                      />
                    </div>
                  )}
                  {reservation.status === 'CONFIRMED' && (
                    <div className="flex flex-col justify-between gap-y-2">
                      <div className="flex gap-x-4 md:justify-end">
                        {moreInfoNeeded && !infoSubmittedAfterRequest && (
                          <SubmitInfoButton reservation={reservation} />
                        )}
                        <CancelButton
                          reservationId={reservation.id}
                          reservationNumber={reservation.reservationNumber}
                          isB2B={isB2BPatient}
                        />
                        <JoinButton reservation={reservation} />
                      </div>

                      <span className="text-xs text-gray-600">
                        {t('appointments.detail.joinNotice', {
                          minutes: minutesBefore,
                        })}
                      </span>
                    </div>
                  )}

                  {reservation.status === 'CANCELED' && (
                    <div className="flex flex-col items-center gap-2">
                      <p className="text-destructive flex items-center justify-center gap-1.5 font-bold">
                        <CircleCheckBig className="h-5 w-5" />
                        {t('appointments.detail.canceled')}
                      </p>
                    </div>
                  )}
                  {reservation.status === 'FINISHED' && (
                    <ReservationFinished reservation={reservation} />
                  )}
                </>
              }
            >
              {moreInfoNeeded && (
                <div className="mt-7">
                  <AdditionalInfo
                    doctorNotes={
                      JSON.parse(moreInfoNeeded.context ?? '{}').requestedInfo
                    }
                  />
                </div>
              )}
            </AppointmentCard>

            <AppointmentTimeline
              timeline={reservation.timeline}
              historyEventMap={historyEventDetails}
            />
          </div>

          <div className="sticky top-4 col-span-1 flex max-h-[calc(100vh-2rem)] flex-col gap-6 self-start overflow-y-auto">
            <PatientInfoCard user={reservationContext.patient} showUsageLimit />
            {!isB2BPatient && (
              <PaymentDetailsCard
                finalAmount={payment.total.shortLabel}
                insuranceDiscount={payment.insuranceDiscount.shortLabel}
                totalAmount={payment.amountDue.shortLabel}
              />
            )}
            {reservation.payment?.status === 'REFUNDED' &&
              reservation.payment.refundedAmount && (
                <div className="rounded-lg border border-green-200 bg-green-50 px-4 py-2 text-center">
                  <p className="text-sm font-medium text-green-800">
                    {t('appointments.detail.refunded', {
                      amount: (
                        reservation.payment.refundedAmount / 100
                      ).toFixed(2),
                      currency:
                        reservation.payment.currency === 'SAR'
                          ? t('appointments.detail.currencySar')
                          : reservation.payment.currency,
                    })}
                  </p>
                </div>
              )}
            {(reservation.status === 'FINISHED' ||
              reservation.status === 'AVAILABLE_FOR_FOLLOWUP') && (
              <DiagnosisCards diagnoses={diagnoses} />
            )}
          </div>
        </div>
      </div>

      <AttachmentsDialog
        open={isAttachmentsDialogOpen}
        onOpenChange={setIsAttachmentsDialogOpen}
        record={reservation}
      />
    </>
  );
}
